import { X } from "lucide-react";
import type { GraphNode, GraphEdge } from "@/hooks/useMemoryGraph";
import { cn } from "@/lib/utils";

const TYPE_COLORS: Record<string, string> = {
  component: "#3fb950",
  pattern: "#a371f7",
  session: "#d29922",
  calibration: "#f0883e",
  story: "#58a6ff",
  epic: "#1f6feb",
  term: "#8b949e",
  principle: "#f778ba",
  decision: "#db6d28",
  guardrail: "#f85149",
  skill: "#79c0ff",
  module: "#56d364",
  bounded_context: "#7ee787",
  requirement: "#d2a8ff",
  layer: "#388bfd",
  architecture: "#3fb950",
  outcome: "#a5d6ff",
  project: "#ffd33d",
};

const TYPE_TO_DOMAIN: Record<string, string> = {
  principle: "Governance",
  guardrail: "Governance",
  requirement: "Governance",
  outcome: "Governance",
  term: "Governance",
  component: "Architecture",
  module: "Architecture",
  bounded_context: "Architecture",
  layer: "Architecture",
  architecture: "Architecture",
  pattern: "Memory",
  calibration: "Memory",
  session: "Memory",
  epic: "Work",
  story: "Work",
  decision: "Work",
  project: "Work",
  skill: "Skills",
};

interface GraphInspectorProps {
  node: GraphNode | undefined | null;
  edges: GraphEdge[];
  onClose: () => void;
  onNavigate: (nodeId: string) => void;
  className?: string;
}

export function GraphInspector({
  node,
  edges,
  onClose,
  onNavigate,
  className,
}: GraphInspectorProps) {
  if (!node) return null;

  const score = (node.properties.effective_score as number) ?? 0;
  const decay = (node.properties.decay_factor as number) ?? 1;
  const n = (node.properties.n as number) ?? 0;
  const k = (node.properties.k as number) ?? 0;
  const domain = TYPE_TO_DOMAIN[node.node_type] ?? "Other";
  const color = TYPE_COLORS[node.node_type] ?? "#484f58";

  const outgoing = edges.filter((e) => e.source === node.node_id);
  const incoming = edges.filter((e) => e.target === node.node_id);

  return (
    <div className={cn("w-[320px] min-w-[260px] max-w-[480px] border-l border-zinc-800 bg-zinc-950 overflow-y-auto flex-shrink-0", className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="inline-block w-3 h-3 rounded-sm flex-shrink-0"
            style={{ backgroundColor: color }}
          />
          <span className="text-sm font-semibold text-zinc-100 truncate">
            {node.label}
          </span>
        </div>
        <button
          onClick={onClose}
          aria-label="Close inspector"
          className="text-zinc-500 hover:text-zinc-300 p-1"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="px-4 py-3 space-y-4">
        {/* Type & domain */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <span
              className="text-xs px-1.5 py-0.5 rounded"
              style={{
                backgroundColor: `${color}20`,
                color,
              }}
            >
              {node.node_type}
            </span>
            <span className="text-xs text-zinc-500">{domain}</span>
          </div>
          {node.source_file && (
            <div className="text-xs text-amber-500/80 font-mono truncate">
              {node.source_file}
            </div>
          )}
        </div>

        {/* Wilson score */}
        {(n > 0 || score > 0) && (
          <div className="space-y-2">
            <div className="text-xs text-zinc-500 font-medium uppercase tracking-wider">
              Score
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-zinc-400">Wilson</span>
                <span className="text-zinc-200 tabular-nums font-medium">
                  {score.toFixed(3)}
                </span>
              </div>
              {/* Wilson CI bar */}
              <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${score * 100}%`,
                    backgroundColor: color,
                  }}
                />
              </div>
              <div className="flex justify-between text-[11px] text-zinc-600">
                <span>
                  {n} obs · {k} positive
                </span>
                <span>decay {decay.toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Edges */}
        {(outgoing.length > 0 || incoming.length > 0) && (
          <div className="space-y-2">
            <div className="text-xs text-zinc-500 font-medium uppercase tracking-wider">
              Edges ({outgoing.length + incoming.length})
            </div>
            <div className="space-y-1 max-h-[200px] overflow-y-auto">
              {outgoing.map((e, i) => (
                <button
                  key={`out-${i}`}
                  onClick={() => onNavigate(e.target)}
                  className="flex items-center gap-1.5 text-xs w-full text-left hover:bg-zinc-800/50 rounded px-1.5 py-1"
                >
                  <span className="text-zinc-600">→</span>
                  <span className="text-zinc-500">{e.edge_type}</span>
                  <span className="text-blue-400 truncate">{e.target}</span>
                </button>
              ))}
              {incoming.map((e, i) => (
                <button
                  key={`in-${i}`}
                  onClick={() => onNavigate(e.source)}
                  className="flex items-center gap-1.5 text-xs w-full text-left hover:bg-zinc-800/50 rounded px-1.5 py-1"
                >
                  <span className="text-zinc-600">←</span>
                  <span className="text-zinc-500">{e.edge_type}</span>
                  <span className="text-blue-400 truncate">{e.source}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Properties */}
        {Object.keys(node.properties).length > 0 && (
          <div className="space-y-2">
            <div className="text-xs text-zinc-500 font-medium uppercase tracking-wider">
              Properties
            </div>
            <div className="space-y-1">
              {Object.entries(node.properties)
                .filter(
                  ([key]) =>
                    !["effective_score", "decay_factor", "n", "k"].includes(
                      key,
                    ),
                )
                .slice(0, 10)
                .map(([key, value]) => (
                  <div
                    key={key}
                    className="flex justify-between text-xs gap-2"
                  >
                    <span className="text-zinc-500 flex-shrink-0">{key}</span>
                    <span className="text-zinc-300 truncate text-right font-mono">
                      {typeof value === "object"
                        ? JSON.stringify(value)
                        : String(value)}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Node ID */}
        <div className="pt-2 border-t border-zinc-800">
          <div className="text-[11px] text-zinc-600 font-mono break-all">
            {node.node_id}
          </div>
        </div>
      </div>
    </div>
  );
}
